import { useMediaQuery } from "@mui/material";
import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_MOBLET,
  BREAKPOINT_TABTOP,
} from "../../../../constants/constants";
import {
  PnlCategoriesEnum,
  CategoryPropsType,
  ReportParams,
} from "../../../../types";
import CollapsibleSubRows from "../CollapsibleSubRows";
import styled from "styled-components/macro";
import { PnlTimes } from "../PnLChart/pnlChartTypes";
import { getMachineBreakDownProps } from "../../reportUtils";
import { GENERIC_PNL_CATEGORY_PROPS_ARR } from "../../../../constants/customers";
import { PnlBreakdown } from "../../../../getters/pnlBreakdownGetter/pnlBreakdownTypes";
import CardWrapperStyles from "../../CardWrapperStyles";
import { useTranslation } from "react-i18next";
import { flightDashboardLinksAtom } from "../../../../store/store";
import { SummaryMetricInput } from "reports/utils/metricUtils";
import MetricAndPnLBreakdown from "./MetricAndPnLBreakdown";
import {
  MACHINE_BREAKDOWN_WIDTH_OFFSET_PERCENT,
  MACHINE_BREAKDOWN_WIDTH_PERCENT,
} from "./metricAndPnLBreakdownUtils";
import { useAtom } from "jotai";
import useEosData from "reports/store/useEosData";
import { MachineBreakdownOutputProps } from "./metricAndPnLBreakdownsTypes";
import { useGeAndLtFromUrl } from "../../reportUtils";
import { useParams } from "react-router";

const MAX_NUM_PROCESSES_TO_SHOW = 10;

export const MachineBreakdownsStyles = styled.div`
  width: ${MACHINE_BREAKDOWN_WIDTH_PERCENT +
  MACHINE_BREAKDOWN_WIDTH_OFFSET_PERCENT}%;
  margin: 0 auto;

  .btnShowMore {
    display: flex;
    align-self: center;
  }

  @media print {
    margin: 1em auto 0;
    width: 90%;
  }

  @media (max-width: ${BREAKPOINT_TABTOP}px), print {
    width: 90%;
  }

  @media (max-width: ${BREAKPOINT_MOBLET}px) {
    width: 90%;
  }
`;

// tslint:disable-next-line: cognitive-complexity
export default function MetricAndPnLBreakdowns({
  machemetryIds,
  shouldHideLess = true,
  categoryPropsArr = GENERIC_PNL_CATEGORY_PROPS_ARR(),
  summaryMetricsInputList = [],
  getCustomSummaryMetricsInputList,
  customSkusTitle,
  getCustomSummaryMetricsInputListWithPnlBreakdown,
  getMachineBreakDownPropsToOverride,
  shouldShowButterfly = true,
  shouldExpand = true,
  shouldEnableToggle = true,
  subheader,
  customHeader,
  showReportLink = false,
}: {
  machemetryIds: string[];
  shouldHideLess?: boolean;
  categoryPropsArr?: CategoryPropsType[];
  summaryMetricsInputList?: SummaryMetricInput[];
  getCustomSummaryMetricsInputList?: (
    machemetryId: string
  ) => SummaryMetricInput[];
  getCustomSummaryMetricsInputListWithPnlBreakdown?: (
    pnlBreakdown: PnlBreakdown
  ) => SummaryMetricInput[];
  customSkusTitle?: string;
  getMachineBreakDownPropsToOverride?: (
    machemetryId: string
  ) => Record<string, unknown>;
  shouldShowButterfly?: boolean;
  shouldExpand?: boolean;
  /** if false, MetricAndPnlBreakdown start in expanded state and cannot collapse */
  shouldEnableToggle?: boolean;
  subheader?: string;
  customHeader?: string;
  showReportLink?: boolean;
}) {
  const { t } = useTranslation("EOSReport");
  const { pnlBreakdownsByProcess, pnlBreakdownsBySKU } = useEosData();
  const { ckey: tenant } = useParams() as ReportParams;
  const range = useGeAndLtFromUrl();

  const isMobileOrSmaller = useMediaQuery(
    `(max-width: ${BREAKPOINT_MOBILE}px)`
  );

  const [flightDashboardLinks] = useAtom(flightDashboardLinksAtom);
  const machineBreakDownArr = getMachineBreakDownProps({
    machemetryIds,
    pnlBreakdownsByProcess,
    pnlBreakdownsBySKU,
    categoryPropsArr,
    flightDashboardLinks,
    getMachineBreakDownPropsToOverride,
  });

  // get max length of process names to use as width and font size weighting
  const maxBreakdownHeaderLength =
    getMaxBreakdownHeaderLength(machineBreakDownArr);

  const machineBreakDownToShow = shouldHideLess
    ? machineBreakDownArr?.filter(
        machineData =>
          machineData &&
          !isAllTimeUncollectedOrNotScheduled(machineData.pnlTimes)
      )
    : machineBreakDownArr;

  const machineBreakDownToHide: MachineBreakdownOutputProps[] | undefined =
    shouldHideLess
      ? machineBreakDownArr?.filter(
          machineData =>
            machineData &&
            isAllTimeUncollectedOrNotScheduled(machineData.pnlTimes)
        )
      : [];

  const rowsToHide = machineBreakDownToHide?.map(props => {
    const customSummaryMetricsInputList = getCustomSummaryMetricsInputList
      ? getCustomSummaryMetricsInputList(props.machemetryId)
      : [];

    return (
      <MetricAndPnLBreakdown
        key={props.machemetryId}
        {...{
          ...props,
          processId: props.machemetryId,
          summaryMetricsInputList: [
            ...summaryMetricsInputList,
            ...customSummaryMetricsInputList,
          ],
          customSkusTitle,
          getCustomSummaryMetricsInputListWithPnlBreakdown,
          showMetricTitle:
            isMobileOrSmaller ||
            !machineBreakDownToShow ||
            machineBreakDownToShow.length <= 0,
          shouldShowButterfly,
          shouldExpand,
          maxBreakdownHeaderLength,
        }}
        reportLink={
          showReportLink
            ? `${window.location.protocol}//${window.location.host}/${tenant}/reports/eos/${props.processName}?ge=${range.ge}&lt=${range.lt}`
            : undefined
        }
      />
    );
  });

  return (
    <CardWrapperStyles>
      <MachineBreakdownsStyles>
        <div className="header">
          {customHeader ?? t("processBreakdownHeader")}
        </div>
        <div className="subHeader">
          {subheader ?? t("processBreakdownSubheader")}
        </div>
        <>
          {machineBreakDownToShow &&
            machineBreakDownToShow?.length > 0 &&
            machineBreakDownToShow.map((props, index) => {
              const customSummaryMetricsInputList =
                getCustomSummaryMetricsInputList
                  ? getCustomSummaryMetricsInputList(props.machemetryId)
                  : [];

              return (
                <MetricAndPnLBreakdown
                  key={props.machemetryId}
                  {...props}
                  showMetricTitle={isMobileOrSmaller || index === 0}
                  {...{
                    summaryMetricsInputList: [
                      ...summaryMetricsInputList,
                      ...customSummaryMetricsInputList,
                    ],
                    getCustomSummaryMetricsInputListWithPnlBreakdown,
                    shouldShowButterfly,
                    shouldExpand,
                    maxBreakdownHeaderLength,
                    shouldEnableToggle,
                    processId: props.machemetryId,
                  }}
                  reportLink={
                    showReportLink
                      ? `${window.location.protocol}//${window.location.host}/${tenant}/reports/eos/${props.processName}?ge=${range.ge}&lt=${range.lt}`
                      : undefined
                  }
                />
              );
            })}
          {rowsToHide &&
            rowsToHide?.length > 0 &&
            (machemetryIds.length > MAX_NUM_PROCESSES_TO_SHOW ? (
              <CollapsibleSubRows
                subRows={rowsToHide}
                showMoreTitle={`See ${rowsToHide.length} more uncollected/not scheduled processes`}
                showLessTitle={`See ${rowsToHide.length} less uncollected/not scheduled processes`}
              />
            ) : (
              rowsToHide
            ))}
        </>
      </MachineBreakdownsStyles>
    </CardWrapperStyles>
  );
}

/**
 * Uses process name and sku names (if applicable) to get max chars for leftmost column title
 *
 * @param {(MachineBreakdownOutputProps[] | null)} machineBreakDownArr
 * @return {*}
 */
function getMaxBreakdownHeaderLength(
  machineBreakDownArr: MachineBreakdownOutputProps[] | null
) {
  const maxBreakdownHeaderLength = Math.max(
    ...(machineBreakDownArr
      ? machineBreakDownArr.map(({ pnlBreakdown }) => {
          const processName = pnlBreakdown?.processNames?.[0];
          return processName ? processName.length : 0;
        })
      : [0])
  );

  // get max length of sku names to use as width and font size weighting
  const listOfSkusWithOnlyOneSku = machineBreakDownArr
    ?.filter(({ skuData }) => skuData?.length === 1)
    .map(({ skuData }) => skuData?.[0]);

  const maxSkuNameLength = Math.max(
    ...(listOfSkusWithOnlyOneSku
      ? listOfSkusWithOnlyOneSku.map(skuDatum => {
          const longestWordInSku = skuDatum?.sku
            ?.split(" ")
            .sort((a, b) => b.length - a.length)[0];
          // If all processes have multiple skus, want default max to be "xx skus"
          return longestWordInSku ? longestWordInSku.length : "x SKUS".length;
        })
      : [0])
  );

  // Max title comes from longest word in sku or process name
  return Math.max(maxBreakdownHeaderLength, maxSkuNameLength);
}

/**
 * Checks if machine breakdown row is all uncollected or not scheduled, or a mix of both
 *
 * @param {PnlTimes[]} pnlTimes
 * @return {*} boolean
 */
export function isAllTimeUncollectedOrNotScheduled(
  pnlTimes: PnlTimes[]
): boolean {
  return pnlTimes
    .filter(
      ({ name }) =>
        ![
          PnlCategoriesEnum.notScheduled,
          PnlCategoriesEnum.uncollected,
        ].includes(name as PnlCategoriesEnum)
    )
    .every(({ time }) => time === 0);
}
